<template>
  <div class="cashier-page" :class="{ 'is-tablet': isTabletMode }">
    <el-row :gutter="16" class="cashier-layout">
      <!-- 左侧：结账清单区 -->
      <el-col :xs="24" :sm="24" :md="8" :lg="8" class="left-pane pane-cart">
        <div class="app-card-lg cashier-panel">
          <div class="panel-header">
            <h2 class="panel-title">
              结账清单（{{ totalQuantity }}件）
            </h2>
            <el-button class="btn-secondary" @click="clearCart" :disabled="!cartItems.length">
              清空购物车
            </el-button>
          </div>

          <div v-if="cartItems.length === 0" class="cart-empty">
            <i class="el-icon-shopping-cart-full cart-empty-icon"></i>
            <div class="cart-empty-text">请在右侧点击商品，加入结账清单</div>
          </div>

          <div v-else class="cart-list">
            <el-table
              :data="cartItems"
              class="app-table cashier-cart-table"
              :show-header="true"
              :border="false"
            >
              <el-table-column label="商品" min-width="45%">
                <template slot-scope="scope">
                  <div class="cart-item-name">
                    {{ scope.row.name }}
                  </div>
                  <div v-if="scope.row.spec_summary" class="cart-item-spec">
                    {{ scope.row.spec_summary }}
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="数量" min-width="25%">
                <template slot-scope="scope">
                  <div class="qty-control">
                    <button class="qty-btn" type="button" @click="changeQty(scope.row, -1)">-</button>
                    <span class="qty-value">{{ scope.row.quantity }}</span>
                    <button class="qty-btn" type="button" @click="changeQty(scope.row, 1)">+</button>
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="小计" min-width="30%">
                <template slot-scope="scope">
                  <div class="app-table-number">¥{{ (scope.row.price * scope.row.quantity).toFixed(2) }}</div>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="cart-footer">
            <div class="cart-summary">
              <div>共 {{ totalQuantity }} 件商品</div>
              <div class="summary-line" v-if="voucherDiscountEstimate > 0">
                <span class="summary-label">兑换券抵扣：</span>
                <span class="cart-discount">-¥{{ voucherDiscountEstimate.toFixed(2) }}</span>
              </div>
              <div class="summary-line">
                <span class="summary-label">合计：</span>
                <span class="cart-total">¥{{ payableAmount.toFixed(2) }}</span>
              </div>
            </div>

            <div class="cart-actions">
              <el-button class="btn-secondary" @click="openCashDialog" :disabled="!canPayNow">
                现金收款
              </el-button>
              <el-button
                class="btn-primary"
                @click="openOnlineDialog"
                :disabled="!canPayNow"
              >
                收款 ¥{{ payableAmount.toFixed(2) }}
              </el-button>
            </div>
          </div>
        </div>
      </el-col>

      <!-- 中间：商品列表区 -->
      <el-col :xs="24" :sm="24" :md="10" :lg="10" class="middle-pane pane-products">
        <div class="app-card-lg cashier-panel">
          <div class="panel-header">
            <h2 class="panel-title">商品列表</h2>
            <el-input
              v-model="productKeyword"
              placeholder="搜索商品名称"
              clearable
              size="small"
              prefix-icon="el-icon-search"
              class="app-input product-search"
            />
          </div>

          <div class="product-list">
            <div
              v-for="p in filteredProducts"
              :key="p.id"
              class="product-card"
              :class="{ disabled: p.stock <= 0, 'has-spec': !!p.has_spec_config }"
              @click="onProductClick(p)"
            >
              <div class="product-info">
                <div class="product-name">
                  {{ p.name }}
                  <span v-if="p.has_spec_config" class="spec-badge">选规格</span>
                </div>
                <div class="product-meta">
                  <span class="product-price">¥{{ Number(p.price).toFixed(2) }}</span>
                  <span class="product-stock">库存 {{ Math.max(0, p.stock) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-col>

      <!-- 右侧：会员登录 + 商品分类 + 快捷操作 -->
      <el-col :xs="24" :sm="24" :md="6" :lg="6" class="right-pane pane-side">
        <div class="app-card-lg cashier-panel">
          <div class="member-login-top">
            <div class="member-login-title">会员登录</div>

            <el-button class="btn-primary scan-member-btn" size="small" @click="openMemberLogin">
              扫会员码登录
            </el-button>

            <el-autocomplete
              v-model="memberKeyword"
              class="member-search"
              :fetch-suggestions="queryMembers"
              :debounce="300"
              placeholder="请输入会员码/手机号/姓名"
              clearable
              value-key="displayLabel"
              :popper-append-to-body="false"
              @select="handleMemberSelect"
            />

            <div v-if="memberKeyword && memberNoMatch" class="member-no-match-hint">
              暂无匹配会员，可前往小程序注册会员
            </div>

            <div v-if="member" class="member-info">
              <div class="member-info-row member-info-head">
                <span class="member-logged-label">已登录会员</span>
                <el-button type="text" class="member-logout-btn" @click="clearMemberSession()">退出会员</el-button>
              </div>
              <div class="member-info-row">
                姓名：<strong>{{ member.name }}</strong>
              </div>
              <div class="member-info-row">
                会员码：<strong>{{ shortMemberCode(member.member_code) }}</strong>
              </div>
              <div class="member-info-row">
                会员等级：<strong>{{ member.level_name }}</strong>
              </div>
              <div class="member-info-row">
                可用券：<strong>{{ member.voucher_count }}</strong> 张
              </div>
              <div v-if="member.vouchers && member.vouchers.length" class="member-voucher-list">
                <div
                  v-for="(v, idx) in member.vouchers.slice(0, 6)"
                  :key="v.id || idx"
                  class="member-voucher-item"
                >
                  {{ v.product_name }} · ¥{{ Number(v.value || 0).toFixed(2) }} × {{ v.remaining }}
                </div>
              </div>
              <div class="member-info-row member-hint">
                收款时将按商品名自动核销对应兑换券；实付金额计入会员积分
              </div>
              <div class="member-info-row member-hint">
                收银台无操作 15 分钟将自动退出当前会员
              </div>
            </div>
          </div>

          <div class="panel-header panel-header-small">
            <h2 class="panel-title">商品分类</h2>
          </div>

          <div class="category-list">
            <button
              v-for="tag in categoryTags"
              :key="tag"
              class="category-tag"
              :class="{ active: activeCategoryTag === tag }"
              @click="activeCategoryTag = tag"
            >
              {{ tag }}
            </button>
          </div>

          <div class="side-section">
            <div class="side-title">快捷操作</div>
            <el-button
              class="btn-secondary"
              size="small"
              @click="clearCart"
              :disabled="!cartItems.length"
            >
              清空购物车
            </el-button>
            <el-button
              class="btn-secondary"
              size="small"
              style="margin-top: 8px"
              @click="openGrouponVerifyDialog"
            >
              团购扫码核销
            </el-button>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 会员登录：扫码弹窗 -->
    <el-dialog
      title="会员登录（扫会员码）"
      :visible.sync="memberLoginVisible"
      width="420px"
      :before-close="handleMemberDialogClose"
      center
    >
      <div class="member-login-dialog">
        <el-input
          v-model="memberToken"
          placeholder="可手动输入会员码 token（扫码优先）"
          clearable
        />

        <div class="member-login-actions">
          <el-button class="btn-secondary" size="small" @click="startMemberScan">
            开始扫码
          </el-button>
          <el-button class="btn-primary" size="small" @click="verifyMemberToken">
            验证并登录
          </el-button>
        </div>

        <div class="scan-area" v-show="memberScanning">
          <video ref="scanVideo" class="scan-video" playsinline></video>
          <div class="scan-hint">请对准二维码，识别后将自动校验</div>
        </div>

        <div v-if="scanError" class="scan-error">{{ scanError }}</div>
      </div>
    </el-dialog>

    <!-- 现金收款弹窗 -->
    <el-dialog
      title="现金收款"
      :visible.sync="cashDialogVisible"
      width="460px"
      :close-on-click-modal="false"
      center
    >
      <div class="pay-dialog">
        <div class="pay-row">
          <span class="pay-label">应付：</span>
          <span class="pay-value">¥{{ payableAmount.toFixed(2) }}</span>
        </div>

        <div class="pay-row">
          <span class="pay-label">实收金额：</span>
          <el-input-number
            v-model="cashReceived"
            :min="0"
            :step="1"
            :precision="2"
            class="pay-input"
          />
        </div>

        <div class="pay-row pay-change">
          <span class="pay-label">找零：</span>
          <span class="pay-change-value">
            ¥{{ (cashReceived - payableAmount).toFixed(2) }}
          </span>
        </div>

        <div class="pay-actions">
          <el-button class="btn-secondary" @click="cashDialogVisible = false">取消</el-button>
          <el-button class="btn-primary" :loading="paySubmitting" @click="confirmCashPay" :disabled="!canCashPay">
            确认收款
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 在线收款弹窗 -->
    <el-dialog
      title="选择支付方式"
      :visible.sync="onlineDialogVisible"
      custom-class="cashier-pay-dialog"
      width="480px"
      :close-on-click-modal="false"
      center
    >
      <div class="pay-dialog">
        <div class="pay-amount-hero">
          <span class="pay-amount-label">应付金额</span>
          <span class="pay-amount-num">¥{{ payableAmount.toFixed(2) }}</span>
        </div>

        <div class="pay-method-block">
          <div class="pay-label">支付方式</div>
          <div class="pay-method-tiles">
            <button
              v-for="m in onlinePayMethods"
              :key="m.value"
              type="button"
              class="pay-method-tile"
              :class="{ active: onlinePaymentMethod === m.value }"
              @click="onlinePaymentMethod = m.value"
            >
              <i :class="m.icon"></i>
              <span>{{ m.label }}</span>
            </button>
          </div>
        </div>

        <div class="pay-actions pay-actions-stretch">
          <el-button class="btn-secondary pay-btn-lg" @click="onlineDialogVisible = false">取消</el-button>
          <el-button
            class="btn-primary pay-btn-lg"
            :loading="paySubmitting"
            @click="confirmOnlinePay"
            :disabled="!canPayNow"
          >
            确认收款
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 团购扫码核销（收银台内） -->
    <el-dialog
      title="团购扫码核销"
      :visible.sync="grouponVerifyVisible"
      width="520px"
      custom-class="groupon-verify-dialog"
      :before-close="handleGrouponDialogClose"
      center
    >
      <div class="gv-dialog">
        <div class="gv-platform-row">
          <button
            v-for="p in grouponPlatforms"
            :key="p.value"
            type="button"
            class="gv-plat-btn"
            :class="{ on: grouponPlatform === p.value }"
            @click="grouponPlatform = p.value"
          >
            {{ p.label }}
          </button>
        </div>

        <div class="gv-field">
          <el-input
            v-model="grouponCode"
            class="gv-input"
            placeholder="扫码或输入券码"
            clearable
            @keyup.enter.native="grouponPrepare"
          />
          <div class="gv-actions">
            <el-button class="gv-btn gv-btn-outline" @click="startGrouponScan">
              {{ grouponScanning ? '停止扫码' : '开始扫码' }}
            </el-button>
            <el-button
              class="gv-btn gv-btn-solid"
              :loading="grouponPreparing"
              @click="grouponPrepare"
            >
              验券
            </el-button>
          </div>
        </div>

        <div class="gv-scan" v-show="grouponScanning">
          <video ref="grouponScanVideo" class="gv-scan-video" playsinline></video>
          <div class="gv-scan-hint">对准二维码，识别后自动填入券码</div>
        </div>
        <div v-if="grouponScanError" class="gv-error">{{ grouponScanError }}</div>

        <div v-if="grouponPrepareResult" class="gv-preview">
          <div class="gv-preview-main">
            <strong>{{ grouponPrepareResult.product_name || '团购券' }}</strong>
            <span class="gv-preview-price">
              ¥{{ Number(grouponPrepareResult.value || 0).toFixed(2) }}
            </span>
          </div>
          <el-button
            class="gv-btn gv-btn-solid gv-btn-block"
            :loading="grouponConsuming"
            @click="grouponConsume"
          >
            确认核销
          </el-button>
        </div>

        <div class="gv-divider">
          <span>手机号查券（美团）</span>
        </div>

        <div class="gv-field gv-field-row">
          <el-input
            v-model="grouponPhone"
            class="gv-input"
            maxlength="11"
            placeholder="输入 11 位手机号"
            clearable
            @keyup.enter.native="grouponQueryMobile"
          />
          <el-button
            class="gv-btn gv-btn-outline gv-btn-query"
            :loading="grouponQuerying"
            @click="grouponQueryMobile"
          >
            查询
          </el-button>
        </div>

        <div
          v-for="item in grouponMobileItems"
          :key="item.id"
          class="gv-mobile-row"
        >
          <div class="gv-mobile-main">
            <span class="gv-plat-tag">{{ item.platform_label }}</span>
            <span class="gv-mobile-name">{{ item.product_name }}</span>
            <span class="gv-mobile-price">¥{{ Number(item.value || 0).toFixed(2) }}</span>
          </div>
          <el-button
            type="text"
            class="gv-link"
            :loading="grouponConsuming"
            @click="grouponConsumeWallet(item)"
          >
            核销
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 选规格 -->
    <el-dialog
      :title="specProduct ? ('选择规格 · ' + specProduct.name) : '选择规格'"
      :visible.sync="specDialogVisible"
      custom-class="cashier-spec-dialog"
      width="560px"
      :close-on-click-modal="false"
      @closed="resetSpecDialog"
    >
      <div v-if="specProduct" class="spec-dialog-body">
        <div class="spec-base-row">
          <span>基础价</span>
          <strong>¥{{ Number(specProduct.price || 0).toFixed(2) }}</strong>
        </div>
        <div
          v-for="g in specViewGroups"
          :key="g._key"
          class="spec-group-block"
        >
          <div class="spec-group-head" :class="{ ok: g.satisfied }">
            {{ g.title_hint }}
          </div>
          <div class="spec-option-list">
            <button
              v-for="op in g.options"
              :key="op._key"
              type="button"
              class="spec-option-btn"
              :class="{
                selected: op.selected,
                soldout: op.sold_out,
                recommend: op.is_recommend
              }"
              :disabled="op.sold_out"
              @click="tapSpecOption(g._key, op._key)"
            >
              <span class="spec-option-name">{{ op.option_name }}</span>
              <span v-if="op.extra_price_text" class="spec-option-extra">{{ op.extra_price_text }}</span>
              <span v-if="op.sold_out" class="spec-option-sold">售罄</span>
              <span v-if="op.qty_badge" class="spec-option-badge">{{ op.qty_badge }}</span>
            </button>
          </div>
        </div>
        <div class="spec-footer-row">
          <div class="spec-total-line">
            合计单价
            <strong>¥{{ specUnitPrice.toFixed(2) }}</strong>
          </div>
          <div class="qty-control spec-qty">
            <button type="button" class="qty-btn" @click="specQty = Math.max(1, specQty - 1)">-</button>
            <span class="qty-value">{{ specQty }}</span>
            <button type="button" class="qty-btn" @click="bumpSpecQty">+</button>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button class="btn-secondary" @click="specDialogVisible = false">取消</el-button>
        <el-button class="btn-primary" :disabled="!specCanConfirm" @click="confirmSpecAdd">
          加入清单 ¥{{ (specUnitPrice * specQty).toFixed(2) }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { formatApiError } from '../utils/apiError'
import { canUseCamera, startQrCameraScan, cameraSupportHint } from '../utils/qrCameraScan'
import { uiMode } from '../utils/uiMode'
import { getUserInfo } from '../utils/auth'
import {
  alertGrouponMaintenanceIfNeeded,
  alertGrouponMaintenance,
  isPrivilegedAdminUser
} from '../utils/grouponGate'

const MEMBER_CACHE_KEY = 'eating_cashier_member_cache_v1'
/** 会员登录无操作自动退出（毫秒） */
const MEMBER_IDLE_MS = 15 * 60 * 1000
const MEMBER_ACTIVITY_EVENTS = ['click', 'keydown', 'touchstart', 'mousemove', 'scroll']

function normalizeIdentity(v) {
  return String(v || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
}

/** 缓存会员是否与当前后台登录账号为同一人（管理员不应自动作为会员登录） */
function isAdminSelfMember(member) {
  if (!member) return false
  const admin = getUserInfo() || {}
  const adminPhones = [admin.phone, admin.mobile, admin.tel]
    .map(normalizeIdentity)
    .filter(Boolean)
  const memberPhone = normalizeIdentity(member.phone)
  if (memberPhone && adminPhones.includes(memberPhone)) return true

  const adminIds = [admin.userId, admin.user_id, admin.id, admin.memberUserId]
    .map((x) => String(x || '').trim())
    .filter(Boolean)
  const memberUid = String(member.user_id || '').trim()
  if (memberUid && adminIds.includes(memberUid)) return true

  return false
}

function groupMax(group) {
  if (group.max_select != null && Number(group.max_select) > 0) {
    return Math.floor(Number(group.max_select))
  }
  return null
}

function groupMin(group) {
  return Math.max(0, Math.floor(Number(group.min_select != null ? group.min_select : 1)))
}

function allowsMulti(group) {
  const max = groupMax(group)
  if (max == null) {
    return groupMin(group) > 1 || group.select_type === 'counter'
  }
  return max > 1
}

function countGroupSelected(groupState) {
  if (!groupState || !groupState.counts) return 0
  let total = 0
  Object.keys(groupState.counts).forEach((k) => {
    total += Math.max(0, Math.floor(Number(groupState.counts[k]) || 0))
  })
  return total
}

function isGroupSatisfied(group, groupState) {
  const selected = countGroupSelected(groupState)
  const min = groupMin(group)
  const max = groupMax(group)
  if (selected < min) return false
  if (max != null && selected > max) return false
  return true
}

function allGroupsSatisfied(groups, selectionMap) {
  const list = Array.isArray(groups) ? groups : []
  for (let i = 0; i < list.length; i++) {
    const g = list[i]
    const key = String(g.id != null ? g.id : i)
    if (!isGroupSatisfied(g, selectionMap[key])) return false
  }
  return true
}

function calcExtraPrice(groups, selectionMap) {
  let extra = 0
  const list = Array.isArray(groups) ? groups : []
  for (let i = 0; i < list.length; i++) {
    const g = list[i]
    const key = String(g.id != null ? g.id : i)
    const state = selectionMap[key] || { counts: {} }
    const opts = Array.isArray(g.options) ? g.options : []
    for (let j = 0; j < opts.length; j++) {
      const op = opts[j]
      const oid = String(op.id != null ? op.id : j)
      const qty = Math.max(0, Math.floor(Number(state.counts[oid]) || 0))
      if (qty > 0) extra += (Number(op.extra_price) || 0) * qty
    }
  }
  return Math.round(extra * 100) / 100
}

function buildSpecSummary(groups, selectionMap) {
  const parts = []
  const list = Array.isArray(groups) ? groups : []
  for (let i = 0; i < list.length; i++) {
    const g = list[i]
    const key = String(g.id != null ? g.id : i)
    const state = selectionMap[key] || { counts: {} }
    const opts = Array.isArray(g.options) ? g.options : []
    const names = []
    for (let j = 0; j < opts.length; j++) {
      const op = opts[j]
      const oid = String(op.id != null ? op.id : j)
      const qty = Math.max(0, Math.floor(Number(state.counts[oid]) || 0))
      if (qty <= 0) continue
      names.push(qty > 1 ? op.option_name + '×' + qty : op.option_name)
    }
    if (names.length) parts.push(names.join('、'))
  }
  return parts.join(' / ')
}

function buildSelectedSpecs(groups, selectionMap) {
  const out = []
  const list = Array.isArray(groups) ? groups : []
  for (let i = 0; i < list.length; i++) {
    const g = list[i]
    const key = String(g.id != null ? g.id : i)
    const state = selectionMap[key] || { counts: {} }
    const opts = Array.isArray(g.options) ? g.options : []
    const selected = []
    for (let j = 0; j < opts.length; j++) {
      const op = opts[j]
      const oid = String(op.id != null ? op.id : j)
      const qty = Math.max(0, Math.floor(Number(state.counts[oid]) || 0))
      if (qty <= 0) continue
      selected.push({
        option_id: op.id,
        option_name: op.option_name,
        extra_price: Number(op.extra_price) || 0,
        quantity: qty
      })
    }
    if (selected.length) {
      out.push({
        group_id: g.id,
        group_name: g.group_name,
        options: selected
      })
    }
  }
  return out
}

function buildCartKey(productId, selectedSpecs) {
  const parts = []
  const list = Array.isArray(selectedSpecs) ? selectedSpecs : []
  for (let i = 0; i < list.length; i++) {
    const g = list[i]
    const opts = (g.options || [])
      .map((o) => String(o.option_id) + 'x' + String(o.quantity))
      .sort()
    parts.push(String(g.group_id) + ':' + opts.join(','))
  }
  parts.sort()
  return String(productId) + '|' + parts.join(';')
}

function initSelectionMap(groups) {
  const map = {}
  const list = Array.isArray(groups) ? groups : []
  for (let i = 0; i < list.length; i++) {
    const g = list[i]
    const key = String(g.id != null ? g.id : i)
    map[key] = { counts: {} }
  }
  return map
}

function applyOptionTap(groups, selectionMap, groupKey, optionKey) {
  const list = Array.isArray(groups) ? groups : []
  let group = null
  for (let i = 0; i < list.length; i++) {
    if (String(list[i].id != null ? list[i].id : i) === String(groupKey)) {
      group = list[i]
      break
    }
  }
  if (!group) return { ok: false, selectionMap }

  const map = Object.assign({}, selectionMap || {})
  const state = map[groupKey] || { counts: {} }
  const counts = Object.assign({}, state.counts || {})
  const current = Math.max(0, Math.floor(Number(counts[optionKey]) || 0))
  const total = countGroupSelected(state)
  const max = groupMax(group)
  const min = groupMin(group)
  const multi = allowsMulti(group)

  if (!multi) {
    if (current > 0) {
      if (min <= 0) {
        map[groupKey] = { counts: {} }
      }
      return { ok: true, selectionMap: map }
    }
    const single = {}
    single[optionKey] = 1
    map[groupKey] = { counts: single }
    return { ok: true, selectionMap: map }
  }

  const next = current + 1
  const projected = total - current + next
  const limit = max != null ? max : 99
  if (projected > limit) {
    if (current > 0) {
      delete counts[optionKey]
      map[groupKey] = { counts }
      return { ok: true, selectionMap: map }
    }
    return { ok: false, selectionMap, toast: '该板块最多选 ' + limit + ' 份' }
  }
  counts[optionKey] = next
  map[groupKey] = { counts }
  return { ok: true, selectionMap: map }
}

function decorateGroupsForView(groups, selectionMap) {
  const list = Array.isArray(groups) ? groups : []
  return list
    .map((g, i) => {
      const key = String(g.id != null ? g.id : i)
      const state = selectionMap[key] || { counts: {} }
      const selectedCount = countGroupSelected(state)
      const multi = allowsMulti(g)
      const min = groupMin(g)
      const max = groupMax(g)
      let range
      if (max == null) {
        range = min > 0 ? '请选至少 ' + min + ' 份' : '可选'
      } else if (min === max) {
        range = '请选 ' + min + ' 份'
      } else {
        range = '请选 ' + min + '-' + max + ' 份'
      }
      const opts = (g.options || [])
        .filter((op) => {
          if (!op) return false
          if (op.enabled === false || op.enabled === 0 || op.enabled === '0') return false
          return true
        })
        .map((op, j) => {
          const oid = String(op.id != null ? op.id : j)
          const qty = Math.max(0, Math.floor(Number(state.counts[oid]) || 0))
          const soldOut = Number(op.stock_status) === 0
          const extraNum = Number(op.extra_price) || 0
          return Object.assign({}, op, {
            _key: oid,
            selected_qty: qty,
            selected: qty > 0,
            qty_badge: qty > 1 ? 'x' + qty : qty === 1 && multi ? 'x1' : '',
            sold_out: soldOut,
            is_recommend: !!(op.is_recommend === true || op.is_recommend === 1 || op.is_recommend === '1'),
            extra_price_text: extraNum > 0 ? '+' + extraNum.toFixed(2) : ''
          })
        })
      return Object.assign({}, g, {
        _key: key,
        title_hint: (g.group_name || '规格') + '（' + range + '，已选 ' + selectedCount + ' 份）',
        selected_count: selectedCount,
        satisfied: isGroupSatisfied(g, state),
        allows_multi: multi,
        options: opts
      })
    })
    .filter((g) => (g.options || []).length > 0)
}

export default {
  name: 'CashierSystem',
  data() {
    return {
      productKeyword: '',
      activeCategoryTag: '全部',
      products: [],
      configCategories: [],

      cartItems: [],

      memberKeyword: '',
      memberNoMatch: false,
      member: null,

      memberLoginVisible: false,
      memberToken: '',
      memberScanning: false,
      scanError: '',
      scanStream: null,

      grouponVerifyVisible: false,
      grouponPlatforms: [
        { value: 'meituan', label: '美团' },
        { value: 'douyin', label: '抖音' },
        { value: 'eleme', label: '饿了么' }
      ],
      grouponPlatform: 'meituan',
      grouponCode: '',
      grouponPhone: '',
      grouponScanning: false,
      grouponScanError: '',
      grouponScanStream: null,
      grouponPreparing: false,
      grouponConsuming: false,
      grouponQuerying: false,
      grouponPrepareResult: null,
      grouponMobileItems: [],

      cashDialogVisible: false,
      cashReceived: 0,

      onlineDialogVisible: false,
      onlinePaymentMethod: 'wechat',
      onlinePayMethods: [
        { value: 'wechat', label: '微信', icon: 'el-icon-chat-dot-round' },
        { value: 'alipay', label: '支付宝', icon: 'el-icon-wallet' },
        { value: 'bank_card', label: '银行卡', icon: 'el-icon-bank-card' }
      ],

      specDialogVisible: false,
      specProduct: null,
      specSelectionMap: {},
      specQty: 1,

      paySubmitting: false,

      /** 当前会员最近一次操作时间（用于 15 分钟无操作退出） */
      memberLastActiveAt: 0,
      memberIdleTimer: null,
      _memberActivityHandler: null,
      _persistActivityScheduled: false
    }
  },
  computed: {
    isTabletMode() {
      return !!(uiMode && uiMode.isTabletMode)
    },
    categoryTags() {
      const cats = Array.isArray(this.configCategories) && this.configCategories.length
        ? this.configCategories
        : ['随心订', '饮品', '超级碗']
      return ['全部'].concat(cats)
    },
    subtotalAmount() {
      return this.cartItems.reduce((sum, it) => sum + it.price * it.quantity, 0)
    },
    totalQuantity() {
      return this.cartItems.reduce((sum, it) => sum + it.quantity, 0)
    },
    payableAmount() {
      return Math.max(0, this.subtotalAmount - this.voucherDiscountEstimate)
    },
    voucherDiscountEstimate() {
      if (!this.member || !Array.isArray(this.member.vouchers) || !this.cartItems.length) return 0
      const pool = this.member.vouchers.map((v) => ({
        name: String(v.product_name || '').replace(/\s+/g, ''),
        value: Number(v.value || 0),
        left: Math.max(0, Number(v.remaining || 0))
      }))
      let discount = 0
      this.cartItems.forEach((it) => {
        const want = String(it.base_name || it.name || '').replace(/\s+/g, '')
        let qty = Math.max(1, Number(it.quantity || 1))
        const unit = Number(it.price || 0)
        while (qty > 0) {
          const hit = pool.find((v) => v.left > 0 && v.name === want)
          if (!hit) break
          discount += Math.min(hit.value, unit)
          hit.left -= 1
          qty -= 1
        }
      })
      return Math.round(discount * 100) / 100
    },
    canPayNow() {
      return !!this.member && this.cartItems.length > 0
    },
    canCashPay() {
      return this.canPayNow && this.cashReceived >= this.payableAmount
    },
    filteredProducts() {
      const kw = (this.productKeyword || '').trim().toLowerCase()
      return this.products.filter((p) => {
        const tag = this.mapProductTag(p)
        const matchTag = this.activeCategoryTag === '全部' || tag === this.activeCategoryTag
        const matchKw = !kw || String(p.name || '').toLowerCase().includes(kw)
        return matchTag && matchKw
      })
    },
    specViewGroups() {
      if (!this.specProduct) return []
      return decorateGroupsForView(this.specProduct.spec_groups || [], this.specSelectionMap)
    },
    specExtra() {
      if (!this.specProduct) return 0
      return calcExtraPrice(this.specProduct.spec_groups || [], this.specSelectionMap)
    },
    specUnitPrice() {
      if (!this.specProduct) return 0
      return Math.round((Number(this.specProduct.price || 0) + this.specExtra) * 100) / 100
    },
    specCanConfirm() {
      if (!this.specProduct) return false
      return allGroupsSatisfied(this.specProduct.spec_groups || [], this.specSelectionMap)
    }
  },
  mounted() {
    this.loadMgmtConfig()
    this.loadProducts()
    this.restoreMemberFromCache()
    this.bindMemberIdleWatch()
  },
  beforeDestroy() {
    this.unbindMemberIdleWatch()
    this.stopMemberScan()
    this.stopGrouponScan()
  },
  methods: {
    safeNumber(v, fallback = 0) {
      const n = Number(v)
      return Number.isFinite(n) ? n : fallback
    },
    async loadMgmtConfig() {
      try {
        const resp = await this.$api.get('/products/mgmt-config')
        if (resp && resp.code === 200 && resp.data && Array.isArray(resp.data.categories)) {
          this.configCategories = resp.data.categories.filter(Boolean)
        }
      } catch (_e) {
        /* 使用默认分类标签 */
      }
    },
    shortMemberCode(code) {
      const s = String(code || '')
      if (!s) return '—'
      if (s.length <= 8) return s
      return `${s.slice(0, 2)}…${s.slice(-4)}`
    },
    mapProductTag(p) {
      const c = String(p.category || '').trim()
      if (c === '饮品' || c.includes('饮') || /drink/i.test(c)) return '饮品'
      if (c === '超级碗' || c === '增肌随心碗') return '超级碗'
      if (c === '随心订') return '随心订'
      return c || '其他'
    },
    async loadProducts() {
      try {
        const resp = await this.$api.get('/cashier/products', { params: { category: '全部' } })
        if (resp && resp.code === 200 && Array.isArray(resp.data)) {
          this.products = resp.data
          return
        }
        this.$message.warning('商品接口返回异常，将展示空列表')
      } catch (e) {
        console.error('[cashier][products] error:', e)
        this.$message.error(`${formatApiError(e, '加载商品失败')}`)
      }
    },

    getCartProductStock(productId) {
      const p = this.products.find((x) => String(x.id) === String(productId))
      return p ? Math.max(0, Math.floor(Number(p.stock || 0))) : 0
    },
    onProductClick(product) {
      const stock = Math.max(0, Math.floor(Number(product.stock || 0)))
      if (stock <= 0) {
        this.$message.warning('该商品库存不足，无法加入结账清单')
        return
      }
      if (product.has_spec_config && Array.isArray(product.spec_groups) && product.spec_groups.length) {
        this.openSpecDialog(product)
        return
      }
      this.addPlainToCart(product)
    },
    openSpecDialog(product) {
      this.specProduct = product
      this.specSelectionMap = initSelectionMap(product.spec_groups || [])
      this.specQty = 1
      this.specDialogVisible = true
    },
    resetSpecDialog() {
      this.specProduct = null
      this.specSelectionMap = {}
      this.specQty = 1
    },
    tapSpecOption(groupKey, optionKey) {
      const result = applyOptionTap(
        (this.specProduct && this.specProduct.spec_groups) || [],
        this.specSelectionMap,
        groupKey,
        optionKey
      )
      if (!result.ok && result.toast) {
        this.$message.warning(result.toast)
        return
      }
      this.specSelectionMap = result.selectionMap
    },
    bumpSpecQty() {
      if (!this.specProduct) return
      const stock = Math.max(0, Math.floor(Number(this.specProduct.stock || 0)))
      if (this.specQty + 1 > stock) {
        this.$message.warning(`库存不足：剩余 ${stock}`)
        return
      }
      this.specQty += 1
    },
    confirmSpecAdd() {
      if (!this.specCanConfirm || !this.specProduct) return
      const groups = this.specProduct.spec_groups || []
      const selected_specs = buildSelectedSpecs(groups, this.specSelectionMap)
      const spec_summary = buildSpecSummary(groups, this.specSelectionMap)
      const unit = this.specUnitPrice
      const cart_key = buildCartKey(this.specProduct.id, selected_specs)
      const stock = Math.max(0, Math.floor(Number(this.specProduct.stock || 0)))
      const qty = Math.max(1, Math.floor(Number(this.specQty) || 1))
      if (qty > stock) {
        this.$message.warning(`库存不足：${this.specProduct.name} 剩余 ${stock}`)
        return
      }
      const idx = this.cartItems.findIndex((it) => String(it.cart_key) === String(cart_key))
      if (idx >= 0) {
        const nextQty = this.cartItems[idx].quantity + qty
        if (nextQty > stock) {
          this.$message.warning(`库存不足：${this.specProduct.name} 剩余 ${stock}`)
          return
        }
        this.cartItems[idx].quantity = nextQty
      } else {
        this.cartItems.push({
          id: this.specProduct.id,
          cart_key,
          base_name: this.specProduct.name,
          name: this.specProduct.name,
          spec_summary,
          selected_specs,
          price: unit,
          quantity: qty
        })
      }
      this.specDialogVisible = false
    },
    addPlainToCart(product) {
      const stock = Math.max(0, Math.floor(Number(product.stock || 0)))
      if (stock <= 0) {
        this.$message.warning('该商品库存不足，无法加入结账清单')
        return
      }
      const cart_key = String(product.id)
      const idx = this.cartItems.findIndex((it) => String(it.cart_key || it.id) === cart_key)
      if (idx >= 0) {
        const nextQty = this.cartItems[idx].quantity + 1
        if (nextQty > stock) {
          this.$message.warning(`库存不足：${product.name} 剩余 ${stock}`)
          return
        }
        this.cartItems[idx].quantity = nextQty
      } else {
        this.cartItems.push({
          id: product.id,
          cart_key,
          base_name: product.name,
          name: product.name,
          spec_summary: '',
          selected_specs: [],
          price: Number(product.price || 0),
          quantity: 1
        })
      }
    },
    changeQty(row, delta) {
      const key = row.cart_key || row.id
      const idx = this.cartItems.findIndex((it) => String(it.cart_key || it.id) === String(key))
      if (idx < 0) return

      const stock = this.getCartProductStock(this.cartItems[idx].id)
      let next = this.cartItems[idx].quantity + delta
      if (!Number.isFinite(next)) next = 1
      if (next <= 0) {
        this.cartItems.splice(idx, 1)
        return
      }
      if (stock > 0) next = Math.min(next, stock)
      this.cartItems[idx].quantity = next
    },
    clearCart() {
      this.cartItems = []
      this.touchMemberActivity()
    },

    bindMemberIdleWatch() {
      this.unbindMemberIdleWatch()
      this._memberActivityHandler = () => this.touchMemberActivity()
      MEMBER_ACTIVITY_EVENTS.forEach((ev) => {
        document.addEventListener(ev, this._memberActivityHandler, { passive: true, capture: true })
      })
      this.memberIdleTimer = setInterval(() => this.checkMemberIdle(), 10000)
    },
    unbindMemberIdleWatch() {
      if (this._memberActivityHandler) {
        MEMBER_ACTIVITY_EVENTS.forEach((ev) => {
          document.removeEventListener(ev, this._memberActivityHandler, true)
        })
        this._memberActivityHandler = null
      }
      if (this.memberIdleTimer) {
        clearInterval(this.memberIdleTimer)
        this.memberIdleTimer = null
      }
    },
    touchMemberActivity() {
      if (!this.member) return
      this.memberLastActiveAt = Date.now()
      if (this._persistActivityScheduled) return
      this._persistActivityScheduled = true
      setTimeout(() => {
        this._persistActivityScheduled = false
        if (this.member) this.persistMemberToCache(this.member)
      }, 3000)
    },
    checkMemberIdle() {
      if (!this.member) return
      const last = Number(this.memberLastActiveAt || 0)
      if (!last) {
        this.memberLastActiveAt = Date.now()
        return
      }
      if (Date.now() - last >= MEMBER_IDLE_MS) {
        this.clearMemberSession({ reason: 'idle' })
      }
    },
    clearMemberSession(opts = {}) {
      this.member = null
      this.memberKeyword = ''
      this.memberNoMatch = false
      this.memberLastActiveAt = 0
      try {
        localStorage.removeItem(MEMBER_CACHE_KEY)
      } catch (_e) {
        /* ignore */
      }
      if (opts.reason === 'idle') {
        this.$message.info('会员登录已超时自动退出（15 分钟无操作）')
      }
    },

    openMemberLogin() {
      this.memberLoginVisible = true
      this.memberToken = this.memberToken || ''
      this.scanError = ''
      this.touchMemberActivity()
    },
    async verifyMemberToken() {
      const token = String(this.memberToken || '').trim()
      if (!token) {
        this.$message.warning('请先输入会员码 token（或点击开始扫码）')
        return
      }
      await this.loginByMemberCode(token)
    },

    async queryMembers(queryString, cb) {
      const kw = String(queryString || '').trim()
      if (!kw) {
        this.memberNoMatch = false
        cb([])
        return
      }

      try {
        const resp = await this.$api.get('/cashier/members', { params: { keyword: kw } })
        const list = resp && resp.code === 200 && Array.isArray(resp.data) ? resp.data : []
        if (!list.length) {
          this.memberNoMatch = true
          cb([{ displayLabel: '暂无匹配会员', user_id: '', isEmpty: true }])
          return
        }

        this.memberNoMatch = false
        cb(list)
      } catch (e) {
        console.error('[cashier][members] error:', e)
        this.memberNoMatch = false
        cb([])
      }
    },

    async handleMemberSelect(item) {
      if (!item || item.isEmpty) return
      if (!item.user_id) return

      const nextMember = {
        user_id: item.user_id,
        member_code: item.member_code || '',
        name: item.name || item.username || '会员',
        phone: item.phone || '',
        level_code: item.level_code || 'S0',
        level_name: item.level_name || item.level_code || 'S0',
        voucher_count: Number(item.voucher_count || 0),
        vouchers: Array.isArray(item.vouchers) ? item.vouchers : []
      }

      this.member = nextMember
      this.memberKeyword = item.displayLabel
      this.memberLastActiveAt = Date.now()
      this.persistMemberToCache(nextMember)
    },

    persistMemberToCache(member) {
      try {
        const payload = Object.assign({}, member || {}, {
          _last_active_at: this.memberLastActiveAt || Date.now()
        })
        localStorage.setItem(MEMBER_CACHE_KEY, JSON.stringify(payload))
      } catch (e) {
        // 忽略本地存储失败
      }
    },
    async restoreMemberFromCache() {
      try {
        const raw = localStorage.getItem(MEMBER_CACHE_KEY)
        if (!raw) return
        const cached = JSON.parse(raw)
        if (!cached || !cached.user_id) return
        // 不自动恢复当前管理员本人的会员身份
        if (isAdminSelfMember(cached)) {
          localStorage.removeItem(MEMBER_CACHE_KEY)
          return
        }
        const last = Number(cached._last_active_at || 0)
        if (last && Date.now() - last >= MEMBER_IDLE_MS) {
          localStorage.removeItem(MEMBER_CACHE_KEY)
          return
        }
        this.memberLastActiveAt = last || Date.now()
        this.member = cached
        this.memberKeyword = cached.name ? cached.name : ''
        // 刷新可用券：优先手机号/姓名，避免过期会员码刷新失败
        const kw = cached.phone || cached.name || cached.user_id
        if (kw) {
          const refreshed = await this.$api.get('/cashier/members', { params: { keyword: kw } })
          const list =
            refreshed && refreshed.code === 200 && Array.isArray(refreshed.data) ? refreshed.data : []
          const hit = list.find((x) => String(x.user_id) === String(cached.user_id))
          if (hit) {
            if (isAdminSelfMember(hit)) {
              this.clearMemberSession()
              return
            }
            await this.handleMemberSelect(hit)
          }
        }
      } catch (e) {
        // ignore
      }
    },

    async loginByMemberCode(code) {
      try {
        const token = String(code || '').trim()
        if (!token) return
        // 优先走扫码校验接口（精确校验有效会员码）
        try {
          const byCode = await this.$api.post('/cashier/member-by-qrcode', { token })
          if (byCode && byCode.code === 200 && byCode.data && byCode.data.user_id) {
            await this.handleMemberSelect(byCode.data)
            this.memberLoginVisible = false
            this.stopMemberScan()
            this.$message.success(
              `会员登录成功，可用兑换券 ${Number(byCode.data.voucher_count || 0)} 张`
            )
            return
          }
        } catch (_e) {
          // 非 UUID / 过期时回落模糊搜索
        }
        const resp = await this.$api.get('/cashier/members', { params: { keyword: token } })
        const list = resp && resp.code === 200 && Array.isArray(resp.data) ? resp.data : []
        if (!list.length) {
          this.$message.error('未找到该会员信息，请检查会员码是否过期或重新出示')
          return
        }
        const exact = list.find((x) => String(x.member_code) === String(token))
        const picked = exact || list[0]
        await this.handleMemberSelect(picked)
        this.memberLoginVisible = false
        this.stopMemberScan()
        this.$message.success(`会员登录成功，可用兑换券 ${Number(picked.voucher_count || 0)} 张`)
      } catch (e) {
        console.error('[cashier][login] error:', e)
        this.$message.error('会员登录失败，请重试')
      }
    },

    async startMemberScan() {
      if (this.memberScanning) return
      this.scanError = ''

      if (!canUseCamera()) {
        this.$message.warning(cameraSupportHint() || '当前浏览器不支持摄像头扫码，请手动输入会员码')
        return
      }

      const videoEl = this.$refs.scanVideo
      if (!videoEl) return

      try {
        this.memberScanning = true
        const session = await startQrCameraScan(videoEl, {
          isActive: () => !!this.memberScanning,
          onResult: async (raw) => {
            this.memberToken = raw
            this.stopMemberScan()
            await this.loginByMemberCode(raw)
          }
        })
        this.scanStream = session.stream
        this._qrScanStop = session.stop
      } catch (e) {
        const name = e && e.name ? String(e.name) : ''
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          this.scanError = '未获得摄像头权限，请在浏览器设置中允许摄像头后重试'
        } else if (String(location.protocol || '') === 'http:' && location.hostname !== 'localhost') {
          this.scanError = '非 HTTPS 环境无法调用摄像头，请使用 https 打开收银台'
        } else {
          this.scanError = '无法打开摄像头，请检查权限后重试，或手动输入会员码'
        }
        this.stopMemberScan()
      }
    },
    stopMemberScan() {
      this.memberScanning = false
      if (typeof this._qrScanStop === 'function') {
        try {
          this._qrScanStop()
        } catch (_e) {}
        this._qrScanStop = null
      }
      if (this.scanStream) {
        try {
          this.scanStream.getTracks().forEach((t) => t.stop())
        } catch (_e) {}
        this.scanStream = null
      }
      const videoEl = this.$refs.scanVideo
      if (videoEl) videoEl.srcObject = null
    },
    handleMemberDialogClose(done) {
      this.stopMemberScan()
      this.scanError = ''
      if (done) done()
    },

    openGrouponVerifyDialog() {
      this.grouponVerifyVisible = true
      this.grouponScanError = ''
      this.grouponPrepareResult = null
      this.$api
        .get('/groupon/status')
        .then((resp) => {
          if (resp && resp.code === 200 && resp.data && resp.data.groupon_enabled === false) {
            if (!isPrivilegedAdminUser()) {
              this.grouponVerifyVisible = false
              alertGrouponMaintenance()
            }
          }
        })
        .catch(() => {})
    },
    handleGrouponDialogClose(done) {
      this.stopGrouponScan()
      this.grouponScanError = ''
      if (done) done()
    },
    stopGrouponScan() {
      this.grouponScanning = false
      if (typeof this._grouponQrScanStop === 'function') {
        try {
          this._grouponQrScanStop()
        } catch (_e) {}
        this._grouponQrScanStop = null
      }
      if (this.grouponScanStream) {
        try {
          this.grouponScanStream.getTracks().forEach((t) => t.stop())
        } catch (_e) {}
        this.grouponScanStream = null
      }
      const videoEl = this.$refs.grouponScanVideo
      if (videoEl) videoEl.srcObject = null
    },
    async startGrouponScan() {
      if (this.grouponScanning) return
      this.grouponScanError = ''
      if (!canUseCamera()) {
        this.$message.warning(cameraSupportHint() || '当前浏览器不支持摄像头扫码')
        return
      }
      const videoEl = this.$refs.grouponScanVideo
      if (!videoEl) return
      try {
        this.grouponScanning = true
        const session = await startQrCameraScan(videoEl, {
          isActive: () => !!this.grouponScanning,
          onResult: async (raw) => {
            this.grouponCode = String(raw || '').trim()
            this.stopGrouponScan()
            await this.grouponPrepare()
          }
        })
        this.grouponScanStream = session.stream
        this._grouponQrScanStop = session.stop
      } catch (e) {
        this.grouponScanError = '无法打开摄像头，请检查权限或使用 HTTPS'
        this.stopGrouponScan()
      }
    },
    async grouponPrepare() {
      const code = String(this.grouponCode || '').trim()
      if (!code) {
        this.$message.warning('请输入或扫描券码')
        return
      }
      this.grouponPreparing = true
      try {
        const resp = await this.$api.post('/groupon/cashier/prepare', {
          platform: this.grouponPlatform,
          code,
          phone: String(this.grouponPhone || '').replace(/\D/g, '')
        })
        if (resp && resp.code === 200 && resp.data) {
          const wallet = resp.data.wallet || {}
          this.grouponPrepareResult = {
            ...wallet,
            groupon_wallet_id: wallet.groupon_wallet_id || wallet.id
          }
          this.$message.success(resp.message || '验券成功')
        } else {
          this.$message.error((resp && resp.message) || '验券失败')
        }
      } catch (e) {
        if (alertGrouponMaintenanceIfNeeded(e)) return
        this.$message.error(formatApiError(e) || '验券失败')
      } finally {
        this.grouponPreparing = false
      }
    },
    async grouponConsume() {
      if (!this.grouponPrepareResult) return
      await this.grouponConsumeWallet(this.grouponPrepareResult)
      this.grouponPrepareResult = null
      this.grouponCode = ''
    },
    async grouponConsumeWallet(item) {
      const wid = (item && (item.groupon_wallet_id || item.id)) || ''
      if (!wid) return
      this.grouponConsuming = true
      try {
        const resp = await this.$api.post('/groupon/cashier/consume', {
          groupon_wallet_id: wid,
          idempotency_key: `cashier-desk:${wid}:${Date.now()}`
        })
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '核销成功')
          this.grouponMobileItems = this.grouponMobileItems.filter(
            (x) => String(x.id || x.groupon_wallet_id) !== String(wid)
          )
        } else {
          this.$message.error((resp && resp.message) || '核销失败')
        }
      } catch (e) {
        if (alertGrouponMaintenanceIfNeeded(e)) return
        this.$message.error(formatApiError(e) || '核销失败')
      } finally {
        this.grouponConsuming = false
      }
    },
    async grouponQueryMobile() {
      const phone = String(this.grouponPhone || '').replace(/\D/g, '')
      if (!/^1\d{10}$/.test(phone)) {
        this.$message.warning('请输入 11 位手机号')
        return
      }
      this.grouponQuerying = true
      this.grouponMobileItems = []
      try {
        const resp = await this.$api.post('/groupon/cashier/query-mobile', { phone })
        if (resp && resp.code === 200) {
          this.grouponMobileItems = (resp.data && resp.data.items) || []
          this.$message.success(
            this.grouponMobileItems.length
              ? `查到 ${this.grouponMobileItems.length} 张`
              : '暂无可用平台券'
          )
        } else {
          this.$message.error((resp && resp.message) || '查询失败')
        }
      } catch (e) {
        if (alertGrouponMaintenanceIfNeeded(e)) return
        this.$message.error(formatApiError(e) || '查询失败')
      } finally {
        this.grouponQuerying = false
      }
    },

    openCashDialog() {
      if (!this.canPayNow) {
        this.$message.warning('请先完成会员登录')
        return
      }
      if (!this.cartItems.length) return
      this.cashReceived = Math.max(0, Number(this.payableAmount.toFixed(2)))
      this.cashDialogVisible = true
    },
    openOnlineDialog() {
      if (!this.canPayNow) {
        this.$message.warning('请先完成会员登录')
        return
      }
      if (!this.cartItems.length) return
      this.onlinePaymentMethod = 'wechat'
      this.onlineDialogVisible = true
    },

    async confirmCashPay() {
      await this.createOrder('cash')
    },
    async confirmOnlinePay() {
      const m = this.onlinePaymentMethod
      await this.createOrder(m)
    },

    async createOrder(payment_method) {
      if (!this.canPayNow) {
        this.$message.warning('请先完成会员登录')
        return
      }
      if (!this.cartItems.length) return

      this.paySubmitting = true
      try {
        const payload = {
          member_user_id: this.member.user_id,
          payment_method,
          paid_amount: this.payableAmount,
          use_vouchers: true,
          cash_received: payment_method === 'cash' ? this.cashReceived : undefined,
          items: this.cartItems.map((it) => ({
            product_id: it.id,
            quantity: it.quantity,
            selected_specs: Array.isArray(it.selected_specs) ? it.selected_specs : []
          }))
        }

        const resp = await this.$api.post('/cashier/orders', payload)
        if (resp && (resp.code === 201 || resp.code === 200)) {
          const redeemed = Number(resp.redeemed_count || 0)
          const points = Number(resp.points_earned || 0)
          const msg =
            (resp.message && String(resp.message).trim()) ||
            (redeemed > 0 ? `收款成功，已核销 ${redeemed} 张兑换券` : '收款成功')
          this.$message.success(points > 0 && !String(msg).includes('积分') ? `${msg}，已获 ${points} 积分` : msg)
          this.clearCart()
          this.cashDialogVisible = false
          this.onlineDialogVisible = false
          this.touchMemberActivity()
          await this.loadProducts()
          // 用 user_id 刷新券数量，避免会员码过期后刷新失败
          if (this.member && this.member.user_id) {
            try {
              const refreshed = await this.$api.get('/cashier/members', {
                params: { keyword: this.member.phone || this.member.name || this.member.user_id }
              })
              const list =
                refreshed && refreshed.code === 200 && Array.isArray(refreshed.data)
                  ? refreshed.data
                  : []
              const hit =
                list.find((x) => String(x.user_id) === String(this.member.user_id)) || null
              if (hit) await this.handleMemberSelect(hit)
            } catch (_e) {
              /* ignore */
            }
          }
          return
        }

        this.$message.error((resp && resp.message) || '收款失败')
      } catch (e) {
        console.error('[cashier][order] error:', e)
        this.$message.error(formatApiError(e, '收款失败，请重试'))
      } finally {
        this.paySubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.cashier-page {
  padding: 16px 20px 16px;
  background: var(--page-bg);
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cashier-layout {
  flex: 1;
  min-height: 0;
  height: 100%;
  align-items: stretch !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.cashier-layout ::v-deep > .el-col {
  height: 100%;
}

.left-pane,
.middle-pane,
.right-pane {
  display: flex !important;
  height: 100%;
  margin-bottom: 0;
}

.cashier-panel {
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: none;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-soft);
  border: 1px solid var(--border-color);
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 10px;
  flex-wrap: wrap;
}

.panel-header-small {
  margin-top: 6px;
}

.panel-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-strong);
  margin: 0;
}

.cart-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  text-align: center;
  min-height: 160px;
}

.cart-empty-icon {
  font-size: 40px;
  margin-bottom: 10px;
  color: var(--primary-color);
}

.cart-empty-text {
  font-size: 12px;
  line-height: 1.5;
  max-width: 180px;
}

.cart-list {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.cashier-cart-table {
  margin-top: 4px;
}

.cart-footer {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
}

.cart-summary {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 14px;
  color: var(--text-body);
}

.summary-line {
  display: flex;
  justify-content: space-between;
}

.summary-label {
  color: #334155;
}

.cart-total {
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-color);
}

.cart-discount {
  font-size: 14px;
  font-weight: 600;
  color: #c0392b;
}

.member-voucher-list {
  margin-top: 8px;
  padding: 8px 10px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  max-height: 120px;
  overflow: auto;
}

.member-voucher-item {
  font-size: 12px;
  color: #555;
  line-height: 1.6;
}

.member-info-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.member-logged-label {
  font-weight: 600;
  color: #0f172a;
}
.member-logout-btn {
  padding: 0;
  color: #64748b;
}
.member-logout-btn:hover {
  color: #dc2626;
}
.member-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #888;
}

.cart-actions {
  display: flex;
  justify-content: stretch;
  gap: 10px;
}

.cart-actions .el-button {
  flex: 1;
  min-height: 44px;
}

.product-search {
  width: min(220px, 100%);
}

.product-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  grid-gap: 12px;
  margin-top: 12px;
  overflow: auto;
  flex: 1;
  min-height: 0;
  padding-right: 4px;
  align-content: start;
}

.product-card {
  display: flex;
  padding: 12px 14px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease, opacity 0.2s ease;
  min-height: 76px;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-soft);
  background: #f3fbf6;
  border-color: var(--primary-color);
}

.product-card.has-spec {
  border-style: dashed;
}

.product-card.disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: #f3f4f6;
}

.product-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
}

.product-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-strong);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.spec-badge {
  font-size: 11px;
  font-weight: 600;
  color: var(--primary-color);
  background: var(--lvyun-accent-soft, rgba(43, 182, 115, 0.14));
  border-radius: 999px;
  padding: 2px 8px;
}

.product-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  gap: 8px;
}

.product-price {
  color: var(--brand-color, var(--primary-color));
  font-weight: 600;
}

.product-stock {
  color: var(--text-subtle);
}

.member-login-top {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.member-login-title {
  font-weight: 800;
  font-size: 14px;
  color: #1d2129;
}

.scan-member-btn {
  width: 100%;
  justify-content: center;
  min-height: 40px;
}

.member-search {
  width: 100%;
}

.member-no-match-hint {
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
  margin-top: -4px;
}

.member-info {
  border-top: 1px dashed rgba(43, 182, 115, 0.35);
  padding-top: 10px;
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #475569;
}

.member-info-row strong {
  color: #111827;
  font-weight: 700;
}

.category-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.category-tag {
  border-radius: 10px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid var(--border-color);
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  min-height: 36px;
}

.category-tag:hover {
  background: #f3fbf6;
  border-color: var(--primary-color);
}

.category-tag.active {
  background: var(--primary-color);
  color: #ffffff;
  border-color: var(--primary-color);
}

.side-section {
  margin-top: auto;
  padding-top: 14px;
}

.side-title {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 10px;
}

.qty-control {
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 2px 4px;
  border: 1px solid var(--border-color);
}

.qty-btn {
  border: none;
  background: transparent;
  width: 28px;
  height: 28px;
  font-size: 16px;
  cursor: pointer;
  color: #475569;
  border-radius: 6px;
}

.qty-btn:hover {
  background: #e2e8f0;
}

.qty-btn:active {
  transform: scale(0.95);
}

.qty-value {
  min-width: 28px;
  text-align: center;
  font-size: 14px;
  color: var(--text-body);
}

.cart-item-name {
  color: var(--text-body);
  word-break: break-all;
  white-space: normal;
  font-weight: 600;
}

.cart-item-spec {
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.member-login-dialog {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.member-login-actions {
  display: flex;
  gap: 10px;
}

.scan-area {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scan-video {
  width: 100%;
  max-height: 240px;
  background: #0b1220;
  border-radius: 8px;
}

.scan-hint {
  font-size: 12px;
  color: #64748b;
}

.scan-error {
  font-size: 12px;
  color: #ef4444;
}

/* —— 团购核销弹窗 —— */
.gv-dialog {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gv-platform-row {
  display: flex;
  gap: 10px;
}

.gv-plat-btn {
  flex: 1;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.gv-plat-btn:hover {
  border-color: #86efac;
  color: #047857;
}

.gv-plat-btn.on {
  border-color: #2bb673;
  background: #ecfdf5;
  color: #047857;
  box-shadow: 0 0 0 3px rgba(43, 182, 115, 0.14);
}

.gv-field {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gv-field-row {
  flex-direction: row;
  align-items: stretch;
  gap: 10px;
}

.gv-input {
  flex: 1;
  min-width: 0;
}

.gv-input >>> .el-input__inner {
  height: 44px;
  line-height: 44px;
  border-radius: 10px;
  border: 1.5px solid #dbe3ea;
  font-size: 14px;
  padding: 0 14px;
}

.gv-input >>> .el-input__inner:focus {
  border-color: #2bb673;
  box-shadow: 0 0 0 3px rgba(43, 182, 115, 0.12);
}

.gv-actions {
  display: flex;
  gap: 10px;
}

.gv-btn {
  height: 42px !important;
  padding: 0 20px !important;
  border-radius: 10px !important;
  font-weight: 700 !important;
  font-size: 14px !important;
}

.gv-btn-outline {
  flex: 1;
  background: #fff !important;
  border: 1.5px solid #2bb673 !important;
  color: #2bb673 !important;
}

.gv-btn-outline:hover,
.gv-btn-outline:focus {
  background: #f0fdf4 !important;
  border-color: #059669 !important;
  color: #047857 !important;
}

.gv-btn-solid {
  flex: 1;
  background: #2bb673 !important;
  border-color: #2bb673 !important;
  color: #fff !important;
}

.gv-btn-solid:hover,
.gv-btn-solid:focus {
  background: #059669 !important;
  border-color: #059669 !important;
  color: #fff !important;
}

.gv-btn-query {
  flex: 0 0 96px;
}

.gv-btn-block {
  width: 100%;
  margin-top: 4px;
}

.gv-scan {
  border-radius: 12px;
  overflow: hidden;
  background: #0f172a;
}

.gv-scan-video {
  width: 100%;
  max-height: 240px;
  display: block;
  object-fit: cover;
  background: #0b1220;
}

.gv-scan-hint {
  padding: 8px 12px;
  font-size: 12px;
  color: #94a3b8;
  background: #1e293b;
}

.gv-error {
  font-size: 13px;
  color: #dc2626;
  margin-top: -4px;
}

.gv-preview {
  padding: 14px 16px;
  border-radius: 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gv-preview-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #0f172a;
}

.gv-preview-price {
  font-weight: 700;
  color: #b45309;
  flex-shrink: 0;
}

.gv-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
}

.gv-divider::before,
.gv-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}

.gv-mobile-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.gv-mobile-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  font-size: 13px;
}

.gv-plat-tag {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: #0f766e;
  background: #ccfbf1;
  padding: 2px 8px;
  border-radius: 4px;
}

.gv-mobile-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #334155;
}

.gv-mobile-price {
  flex-shrink: 0;
  font-weight: 700;
  color: #b45309;
}

.gv-link {
  color: #2bb673 !important;
  font-weight: 700 !important;
  padding: 0 4px !important;
}

.pay-dialog {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.pay-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.pay-label {
  color: #334155;
  font-weight: 600;
}

.pay-value {
  font-weight: 800;
  color: var(--primary-color);
  font-size: 20px;
}

.pay-amount-hero {
  text-align: center;
  padding: 16px 12px;
  border-radius: 14px;
  background: linear-gradient(180deg, #f3fbf6 0%, #ffffff 100%);
  border: 1px solid rgba(43, 182, 115, 0.2);
}

.pay-amount-label {
  display: block;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 6px;
}

.pay-amount-num {
  font-size: 28px;
  font-weight: 800;
  color: var(--primary-color);
  letter-spacing: 0.02em;
}

.pay-change-value {
  font-weight: 800;
  color: #16a34a;
}

.pay-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.pay-actions-stretch .pay-btn-lg {
  flex: 1;
  min-height: 44px;
}

.pay-method-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pay-method-tiles {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.pay-method-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 72px;
  padding: 12px 8px;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #334155;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.pay-method-tile i {
  font-size: 22px;
}

.pay-method-tile:hover {
  border-color: var(--primary-color);
  background: #f3fbf6;
}

.pay-method-tile.active {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: #fff;
  box-shadow: 0 8px 20px rgba(43, 182, 115, 0.25);
}

.spec-dialog-body {
  max-height: 58vh;
  overflow: auto;
  padding-right: 4px;
}

.spec-base-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
  font-size: 14px;
  color: #64748b;
}

.spec-base-row strong {
  color: var(--primary-color);
  font-size: 16px;
}

.spec-group-block {
  margin-bottom: 16px;
}

.spec-group-head {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 10px;
}

.spec-group-head.ok {
  color: var(--primary-color);
}

.spec-option-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.spec-option-btn {
  position: relative;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 13px;
  color: #334155;
  cursor: pointer;
  min-height: 36px;
}

.spec-option-btn.recommend {
  border-color: rgba(43, 182, 115, 0.45);
}

.spec-option-btn.selected {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.spec-option-btn.soldout {
  opacity: 0.55;
  cursor: not-allowed;
  text-decoration: line-through;
  background: #f1f5f9;
}

.spec-option-sold {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #94a3b8;
}

.spec-option-extra {
  margin-left: 4px;
  opacity: 0.85;
  font-size: 12px;
}

.spec-option-badge {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 700;
}

.spec-footer-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}

.spec-total-line {
  font-size: 14px;
  color: #64748b;
}

.spec-total-line strong {
  margin-left: 8px;
  color: var(--primary-color);
  font-size: 20px;
}

/* 平板 / 窄屏自适应 */
@media (max-width: 1100px) {
  .cashier-page {
    padding: 12px;
    height: auto;
    min-height: 100%;
    overflow: auto;
  }

  .cashier-layout {
    height: auto;
    min-height: auto;
  }

  .cashier-layout ::v-deep > .el-col {
    height: auto;
  }

  .left-pane,
  .middle-pane,
  .right-pane {
    height: auto;
    margin-bottom: 12px;
  }

  .cashier-panel {
    height: auto;
    min-height: 320px;
    max-height: none;
  }

  .product-list {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}

@media (max-width: 768px) {
  .cashier-layout {
    min-height: auto;
  }

  .cart-actions {
    flex-direction: column;
  }

  .pay-method-tiles {
    grid-template-columns: 1fr;
  }
}

.cashier-page.is-tablet .cashier-panel {
  padding: 18px;
  border-radius: 16px;
}

.cashier-page.is-tablet .product-card,
.cashier-page.is-tablet .category-tag,
.cashier-page.is-tablet .qty-btn,
.cashier-page.is-tablet .pay-method-tile,
.cashier-page.is-tablet .spec-option-btn {
  min-height: 44px;
}

.cashier-page.is-tablet .product-name,
.cashier-page.is-tablet .category-tag,
.cashier-page.is-tablet .panel-title {
  font-size: 16px;
}

.cashier-page.is-tablet .product-list {
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  grid-gap: 14px;
}

.cashier-page.is-tablet .cart-actions .el-button,
.cashier-page.is-tablet .scan-member-btn {
  min-height: 48px;
  font-size: 16px;
}
</style>

<style>
/* 弹窗挂到 body，需非 scoped */
.groupon-verify-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.groupon-verify-dialog .el-dialog__header {
  padding: 20px 24px 8px;
}

.groupon-verify-dialog .el-dialog__title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}

.groupon-verify-dialog .el-dialog__headerbtn {
  top: 18px;
  right: 18px;
}

.groupon-verify-dialog .el-dialog__body {
  padding: 12px 24px 24px;
}
</style>

